import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PhoneInput from '../components/PhoneInput';
import { tutorsAPI, resolveImage , translationAPI} from '../utils/api';
import { useTranslation } from 'react-i18next';

// Helper to safely extract text based on current language
const tField = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) return field.join(' • '); // For string arrays
  
  const targetLang = lang.startsWith('zh') ? 'zh' : 'en';
  const fallbackLang = targetLang === 'en' ? 'zh' : 'en';

  if (field[targetLang]) {
    if (Array.isArray(field[targetLang])) return field[targetLang].join(' • ');
    return field[targetLang];
  }
  if (field[fallbackLang]) {
    if (Array.isArray(field[fallbackLang])) return field[fallbackLang].join(' • ');
    return field[fallbackLang];
  }
  return '';
};


function TutorModal({ onClose, onSave, tutor = null }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const isEdit = !!tutor;
  const [form, setForm] = useState({ 
    name: { en: '', zh: '' }, 
    email: '', 
    designation: { en: t('faculty.instructor') || 'Instructor', zh: '導師' }, 
    mobile: '+852 ', 
    specialization: { en: ['Jazz', 'Contemporary'], zh: ['爵士舞', '現代舞'] }, 
    experience: '', 
    bio: { en: '', zh: '' }, 
    photoUrl: '', 
    salary: 0, emergencyContact: '+852 ', status: 'Active', joiningDate: new Date().toISOString().split('T')[0]
  });

  const [specInput, setSpecInput] = useState({ en: '', zh: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(tutor?.photoUrl || '');
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(false);

  // Handle initialization for edit mode
  useEffect(() => {
    if (tutor) {
      setForm({
        ...tutor,
        name: typeof tutor.name === 'string' ? { en: tutor.name, zh: '' } : tutor.name || { en: '', zh: '' },
        designation: typeof tutor.designation === 'string' ? { en: tutor.designation, zh: '' } : tutor.designation || { en: '', zh: '' },
        bio: typeof tutor.bio === 'string' ? { en: tutor.bio, zh: '' } : tutor.bio || { en: '', zh: '' },
        specialization: Array.isArray(tutor.specialization) && (tutor.specialization.length === 0 || typeof tutor.specialization[0] === 'string') 
          ? { en: tutor.specialization, zh: [] } 
          : tutor.specialization || { en: [], zh: [] }
      });
      // If editing and ZH fields exist, mark as translated
      if (tutor.name?.zh || tutor.designation?.zh || tutor.bio?.zh || tutor.specialization?.zh?.length > 0) {
        setTranslated(true);
      }
    }
  }, [tutor]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') return setError('PNG or JPEG images only.');
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleTranslateAll = async () => {
    const fieldsToTranslate = ['name', 'designation', 'bio'];
    
    // Only attempt to translate fields that have at least one side filled
    const fieldsWithData = fieldsToTranslate.filter(f => form[f]?.en?.trim() || form[f]?.zh?.trim());
    const specHasData = (form.specialization?.en?.length > 0) || (form.specialization?.zh?.length > 0);

    if (fieldsWithData.length === 0 && !specHasData) {
      return setError('Please fill in at least one field before translating.');
    }

    setTranslating(true);
    setError('');
    try {
      const promises = fieldsWithData.map(f => {
        const enStr = form[f]?.en?.trim();
        const zhStr = form[f]?.zh?.trim();
        
        // Only translate if one side is missing
        if (zhStr && !enStr) {
          return translationAPI.translate(zhStr, 'zh-TW', 'en')
            .then(d => ({ field: f, en: d.responseData?.translatedText || '' }))
            .catch(() => ({ field: f }));
        } else if (enStr && !zhStr) {
          return translationAPI.translate(enStr, 'en', 'zh-TW')
            .then(d => ({ field: f, zh: d.responseData?.translatedText || '' }))
            .catch(() => ({ field: f }));
        }
        return Promise.resolve({ field: f }); // Both filled, no translation needed
      });
      
      // Specialization translation
      if (specHasData) {
        const specEn = form.specialization?.en || [];
        const specZh = form.specialization?.zh || [];
        
        // If one side is completely empty, translate the whole array
        if (specZh.length > 0 && specEn.length === 0) {
          promises.push(
            translationAPI.translate(specZh.join(' | '), 'zh-TW', 'en')
              .then(d => {
                 const translatedStr = d.responseData?.translatedText || '';
                 const translatedArr = translatedStr.split('|').map(s => s.trim()).filter(Boolean);
                 return { field: 'specialization', en: translatedArr };
              })
              .catch(() => ({ field: 'specialization' }))
          );
        } else if (specEn.length > 0 && specZh.length === 0) {
          promises.push(
            translationAPI.translate(specEn.join(' | '), 'en', 'zh-TW')
              .then(d => {
                 const translatedStr = d.responseData?.translatedText || '';
                 const translatedArr = translatedStr.split('|').map(s => s.trim()).filter(Boolean);
                 return { field: 'specialization', zh: translatedArr };
              })
              .catch(() => ({ field: 'specialization' }))
          );
        } else {
           // If both have items, we don't overwrite user's manual input
           promises.push(Promise.resolve({ field: 'specialization' }));
        }
      }

      const results = await Promise.all(promises);
      const updates = {};
      results.forEach(res => {
         if (res.zh) updates[res.field] = { ...form[res.field], zh: res.zh };
         if (res.en) updates[res.field] = { ...form[res.field], en: res.en };
      });
      
      const nextForm = { ...form, ...updates };
      setForm(nextForm);
      
      // Only set fully translated if all required fields are filled out in both languages
      const missingAfter = fieldsToTranslate.filter(f => !nextForm[f]?.en?.trim() || !nextForm[f]?.zh?.trim());
      const specMissingAfter = (!nextForm.specialization?.en || nextForm.specialization.en.length === 0) || (!nextForm.specialization?.zh || nextForm.specialization.zh.length === 0);
      
      if (missingAfter.length === 0 && !specMissingAfter) {
        setTranslated(true);
      } else {
        const missStr = missingAfter.join(', ');
        setError(`Translation incomplete. Missing language data for: ${missStr || 'specialization'}`);
      }
    } catch(err) {
      setError('Translation failed. Please try manually.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name?.en?.trim()) return setError(t('faculty.errFullName') || 'Name (EN) required');
    if (!form.name?.zh?.trim()) return setError('Name (ZH) required. Please translate.');
    if (!form.email?.trim()) return setError(t('faculty.errEmailReq') || 'Email required');
    const mobileDigits = form.mobile.replace(/\D/g, '');
    if (mobileDigits.length < 8) return setError('Please enter a valid mobile number.');
    if (!form.designation?.en?.trim()) return setError(t('faculty.errDesigReq') || 'Designation (EN) required');
    if (!form.designation?.zh?.trim()) return setError('Designation (ZH) required. Please translate.');
    if (!form.bio?.en?.trim()) return setError(t('faculty.errBioReq') || 'Bio (EN) required');
    if (!form.bio?.zh?.trim()) return setError('Bio (ZH) required. Please translate.');
    if (form.salary < 0) return setError(t('faculty.errSalaryNeg') || 'Salary cannot be negative');
    if (!isEdit && !selectedFile) return setError(t('faculty.errPhotoReq') || 'Photo required');
    if (!form.specialization?.en || form.specialization.en.length === 0) return setError('Please add at least one specialization in English.');
    if (!form.specialization?.zh || form.specialization.zh.length === 0) return setError('Please add at least one specialization in Chinese.');
    if (!translated) return alert('Please click ✨ Translate All to Chinese before saving.');

    setLoad(true);
    try {
      const { _id, createdAt, updatedAt, __v, userId, batches, ...cleanForm } = form;
      let res;
      if (isEdit) {
        res = await tutorsAPI.update(tutor._id, cleanForm);
        if (selectedFile) {
          const photoData = new FormData();
          photoData.append('photo', selectedFile);
          await tutorsAPI.uploadPhoto(tutor._id, photoData);
        }
      } else {
        const formData = new FormData();
        Object.keys(cleanForm).forEach(key => {
          if (key === 'name' || key === 'designation' || key === 'bio') {
             formData.append(`${key}[en]`, cleanForm[key].en);
             formData.append(`${key}[zh]`, cleanForm[key].zh);
          } else if (key === 'specialization') {
             cleanForm.specialization.en.forEach(v => formData.append('specialization[en][]', v));
             cleanForm.specialization.zh.forEach(v => formData.append('specialization[zh][]', v));
          } else {
            formData.append(key, cleanForm[key]);
          }
        });
        if (selectedFile) formData.append('photo', selectedFile);
        res = await tutorsAPI.create(formData);
      }
      onSave(res.data);
      onClose();
    } catch(err) { setError(err.response?.data?.message || 'Failed to save.'); } finally { setLoad(false); }
  };

  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 800 }}>
        <div className="dash-modal-header">
          <div className="dash-modal-title">{isEdit ? 'Edit Faculty Member' : 'Register New Faculty'}</div>
          <button className="dash-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="dash-modal-body">
          {error && <div className="form-status-box error mb-4">{error}</div>}

          {/* ✨ One-click Translate All button ✨ */}
          <div style={{ background: translated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${translated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              {translated ? (
                <div style={{ color: '#4caf86', fontWeight: 600, fontSize: 14 }}>✅ Chinese Translations Generated & Locked!</div>
              ) : (
                <div style={{ color: 'var(--wine)', fontWeight: 600, fontSize: 14 }}>Missing Chinese Translations</div>
              )}
              {!translated && (
                  <div style={{ fontSize: 12, color: '#888' }}>Fill fields in either language below, then click to auto-translate.</div>
              )}
            </div>
            <button 
              type="button" 
              onClick={handleTranslateAll}
              disabled={translating}
              style={{ background: translated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: translating ? 0.7 : 1, minWidth: 120 }}
            >
              {translating ? '⏳ Translating...' : translated ? '🔄 Re-translate' : '✨ Translate All'}
            </button>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('faculty.fullName')} (EN)</label>
              <input className="form-input-style" value={form.name?.en || ''} onChange={e => { setForm(p=>({...p,name:{...p.name, en:e.target.value}})); setTranslated(false); }} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.fullName')} (ZH)</label>
              <input className="form-input-style" value={form.name?.zh || ''} onChange={e => { setForm(p=>({...p,name:{...p.name, zh:e.target.value}})); setTranslated(false); }} />
            </div>
          </div>
          
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('faculty.role')} (EN)</label>
              <input className="form-input-style" placeholder="e.g. Senior Instructor" value={form.designation?.en || ''} onChange={e => { setForm(p=>({...p,designation:{...p.designation, en:e.target.value}})); setTranslated(false); }} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.role')} (ZH)</label>
              <input className="form-input-style" placeholder="e.g. 高級導師" value={form.designation?.zh || ''} onChange={e => { setForm(p=>({...p,designation:{...p.designation, zh:e.target.value}})); setTranslated(false); }} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-style">{t('faculty.email')}</label>
            <input className="form-input-style" type="email" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} />
          </div>
          
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('faculty.mobile')}</label>
              <PhoneInput value={form.mobile} onChange={val => setForm(p=>({...p,mobile:val}))} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.emergency')}</label>
              <PhoneInput value={form.emergencyContact} onChange={val => setForm(p=>({...p,emergencyContact:val}))} />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label-style">{t('faculty.experience')}</label>
            <select 
              className="form-input-style" 
              value={form.experience ? (String(form.experience).includes('+') ? '20+' : String(form.experience).replace(/\D/g, '')) : ''}
              onChange={e => setForm(p => ({ ...p, experience: e.target.value }))}
            >
              <option value="">{t('faculty.selectExp')}</option>
              {[...Array(20).keys()].map(i => <option key={i} value={i}>{i} Years</option>)}
              <option value="20+">20+ Years</option>
            </select>
          </div>
          
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('faculty.bio')} (EN)</label>
              <textarea className="form-input-style" rows={3} value={form.bio?.en || ''} onChange={e => { setForm(p=>({...p,bio:{...p.bio, en:e.target.value}})); setTranslated(false); }} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.bio')} (ZH)</label>
              <textarea className="form-input-style" rows={3} value={form.bio?.zh || ''} onChange={e => { setForm(p=>({...p,bio:{...p.bio, zh:e.target.value}})); setTranslated(false); }} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-style">{t('faculty.specializations')}</label>
            <div style={{ display:'flex', gap:16 }}>
              {/* EN Spec */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, marginBottom: 4, color: '#888' }}>English</div>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input className="form-input-style" value={specInput.en} onChange={e => setSpecInput(p => ({...p, en: e.target.value}))} onKeyPress={e => { if(e.key === 'Enter' && specInput.en.trim()) { setForm(p=>({...p,specialization:{...p.specialization, en:[...(p.specialization?.en || []), specInput.en.trim()]}})); setSpecInput(p => ({...p, en: ''})); setTranslated(false); } }} />
                  <button className="btn btn-outline" onClick={() => { if(specInput.en.trim()) { setForm(p=>({...p,specialization:{...p.specialization, en:[...(p.specialization?.en || []), specInput.en.trim()]}})); setSpecInput(p => ({...p, en: ''})); setTranslated(false); } }}>Add</button>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {form.specialization?.en?.map((s, i) => (
                    <span key={i} className="badge badge-gold" style={{ cursor:'pointer' }} onClick={() => {
                        setForm(p=>({
                          ...p,
                          specialization:{
                             en: p.specialization.en.filter((_, idx) => idx !== i),
                             zh: p.specialization.zh
                          }
                        }));
                        setTranslated(false);
                    }}>{s} ✕</span>
                  ))}
                </div>
              </div>
              
              {/* ZH Spec */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, marginBottom: 4, color: '#888' }}>Chinese</div>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input className="form-input-style" value={specInput.zh} onChange={e => setSpecInput(p => ({...p, zh: e.target.value}))} onKeyPress={e => { if(e.key === 'Enter' && specInput.zh.trim()) { setForm(p=>({...p,specialization:{...p.specialization, zh:[...(p.specialization?.zh || []), specInput.zh.trim()]}})); setSpecInput(p => ({...p, zh: ''})); } }} />
                  <button className="btn btn-outline" onClick={() => { if(specInput.zh.trim()) { setForm(p=>({...p,specialization:{...p.specialization, zh:[...(p.specialization?.zh || []), specInput.zh.trim()]}})); setSpecInput(p => ({...p, zh: ''})); } }}>Add</button>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {form.specialization?.zh?.map((s, i) => (
                    <span key={i} className="badge badge-gold" style={{ cursor:'pointer' }} onClick={() => {
                        setForm(p=>({
                          ...p,
                          specialization:{
                             en: p.specialization.en,
                             zh: p.specialization.zh.filter((_, idx) => idx !== i)
                          }
                        }));
                    }}>{s} ✕</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-row-3" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.photo')}</label>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div onClick={() => fileInputRef.current?.click()} style={{ width:50, height:50, background:'#eee', borderRadius:4, overflow:'hidden', cursor:'pointer' }}>
                  {previewUrl ? <img src={resolveImage(previewUrl)} alt="P" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ padding:15 }}>+</span>}
                </div>

                <input type="file" ref={fileInputRef} hidden accept="image/png, image/jpeg" onChange={handleFileUpload} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.salary')}</label>
              <input className="form-input-style" type="number" value={form.salary} onChange={e => setForm(p=>({...p,salary:Number(e.target.value)}))} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('faculty.accountStatus')}</label>
              <select className="form-input-style" value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                <option value="Active">{t('faculty.active')}</option>
                <option value="Inactive">{t('faculty.inactive')}</option>
              </select>
            </div>
          </div>
          {!isEdit && <div className="dash-info-box mt-4"><span>ℹ️</span> Password: welcome@ets</div>}
        </div>
        <div className="dash-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>{t('faculty.cancel')}</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading || !translated}>
             {loading ? t('faculty.saving') : !translated ? '✨ Translate First' : isEdit ? t('faculty.updateBtn') : t('faculty.createBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Faculty() {
  const { t, i18n } = useTranslation();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState({ show: false, tutor: null });
  const navigate = useNavigate();

  const fetchIdRef = React.useRef(0);

  const load = (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    setError('');
    tutorsAPI.getAll()
      .then(r => {
        if (isMounted && currentId === fetchIdRef.current) setTutors(Array.isArray(r.data) ? r.data : []);
      })
      .catch(err => {
        if (isMounted && currentId === fetchIdRef.current) setError(err.response?.data?.message || 'Failed to load faculty. Please try again.');
      })
      .finally(() => {
        if (isMounted && currentId === fetchIdRef.current) setLoad(false);
      });
  };

  useEffect(() => {
    let isMounted = true;
    load(isMounted);
    return () => { isMounted = false; };
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('faculty.deleteConfirm'))) return;
    try {
      await tutorsAPI.delete(id);
      setTutors(p => p.filter(t => t._id !== id));
    } catch { alert(t('faculty.deleteFailed')); }
  };

  const toggleStatus = async (t, e) => {
    e.stopPropagation();
    const newStatus = t.status === 'Active' ? 'inactive' : 'active';
    try {
      await tutorsAPI.toggleStatus(t._id, newStatus);
      setTutors(p => p.map(x => x._id === t._id ? { ...x, status: newStatus === 'active' ? 'Active' : 'Inactive' } : x));
    } catch { alert(t('faculty.statusFailed')); }
  };

  return (
    <Layout title={t('faculty.title')}>
      {modal.show && (
        <TutorModal 
          tutor={modal.tutor} 
          onClose={() => setModal({ show: false, tutor: null })} 
          onSave={() => load()} 
        />
      )}

      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('faculty.title')}</h1>
          <p className="text-sm text-muted">{t('faculty.subtitle')}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({ show: true, tutor: null })}>{t('faculty.addTutor')}</button>
      </div>

      <div className="dash-card">
        {loading ? (
          <div className="p-8 text-center">{t('faculty.loading')}</div>
        ) : error ? (
          <div className="dash-empty" style={{ padding: '60px 20px' }}>
            <div className="dash-empty-icon">⚠️</div>
            <div className="dash-empty-title">{t('faculty.loadError')}</div>
            <p className="dash-empty-sub">{error}</p>
            <button className="btn btn-primary btn-sm" onClick={load} style={{ marginTop: 16 }}>{t('faculty.retry')}</button>
          </div>
        ) : tutors.length === 0 ? (
          <div className="dash-empty" style={{ padding: '60px 20px' }}>
            <div className="dash-empty-icon">👩‍🏫</div>
            <div className="dash-empty-title">{t('faculty.noFaculty')}</div>
            <p className="dash-empty-sub">{t('faculty.clickToAdd')}</p>
          </div>
        ) : tutors.map(tutor => (
          <div key={tutor._id} className="dash-list-item" style={{ cursor:'default', flexWrap:'wrap' }}>
            <div className="dash-avatar" style={{ backgroundImage: tutor.photoUrl ? `url("${resolveImage(tutor.photoUrl)}")` : 'none' }}>
              {!tutor.photoUrl && (tField(tutor.name, i18n.language)[0] || '?')}
            </div>
            <div className="dash-list-item-main" style={{ cursor:'pointer', minWidth:'200px' }} onClick={() => navigate(`/faculty/${tutor._id}`)}>
              <div className="dash-list-item-name">{tField(tutor.name, i18n.language)} <span className={`badge ${tutor.status === 'Active' ? 'badge-success' : 'badge-danger'}`} style={{ marginLeft:8 }}>{tutor.status === 'Active' ? t('faculty.active') : t('faculty.inactive')}</span></div>
              <div className="dash-list-item-sub" style={{ color: '#d2ac67', fontWeight:'600', marginBottom:2 }}>{tField(tutor.designation, i18n.language) || t('faculty.instructor')}</div>
              <div className="dash-list-item-sub">{tField(tutor.specialization, i18n.language) || t('faculty.noSpec')}</div>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); setModal({ show: true, tutor: tutor }); }}>{t('faculty.edit')}</button>
              <button className={`btn btn-sm ${tutor.status === 'Active' ? 'btn-outline' : 'btn-primary'}`} style={{ borderColor: tutor.status === 'Active' ? '#e05252' : '' }} onClick={(e) => toggleStatus(tutor, e)}>
                {tutor.status === 'Active' ? t('faculty.deactivate') : t('faculty.activate')}
              </button>
              <button className="btn btn-sm btn-outline" style={{ color: '#e05252' }} onClick={(e) => handleDelete(tutor._id, e)}>{t('faculty.delete')}</button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
